import { isEmptyVal } from '@/utils';
import { get } from 'lodash';
import { BASE_PLAN_SUB_TYPES, planTypes } from '@/common/constants';
import { flowTypes } from '@/common/service/page-config';
import { getPlanTypeByCategory } from '@/common/service/parse';
import { SHARE_TYPE_ENUM } from '@/views/config/shopping-cart/share-link.vue';
import { planQueryParser } from '@/common/service/flow/utils';

function add2cartConfigParse(config) {
  const { from_sub, from, campaign_code, trial_code /* FREE TRIAL - free trial plan expired need to add paid plan */ } = config;
  const _from = from || from_sub
  const { fromType, category_codes } = planQueryParser(_from);
  if (
    category_codes.length > 1 &&
    ![
      planTypes.ZOOM_PHONE,
      planTypes.ZOOM_PHONE$ZPA,
      planTypes.ZOOM_PHONE$ZPI,
      planTypes.ZONE_EDU_PLUS,
      planTypes.ZONE_PRO_PLUS,
      planTypes.ZOOM_ONE_BUNDLE
    ].includes(fromType)
  ) {
    return {
      type: flowTypes.add2cart,
      editConfig: {
        planType: fromType,
        category: category_codes[0],
        isMultiple: true,
        categories: category_codes,
        campaign_code,
        trial_code
      },
    }
  }

  return {
    type: flowTypes.add2cart,
    editConfig: {
      planType: fromType,
      category: category_codes[0],
      isMultiple: false,
      categories: category_codes,
      campaign_code,
      trial_code
    },
  };
}

function editConfigParse(config) {
  const { type, plan, oldPlanCode, subPlan } = config;
  const isBase = subPlan === 'none';
  const category = isBase ? plan : subPlan
  const planType = getPlanTypeByCategory(category);
  return {
    type,
    editConfig: {
      plan,
      oldPlanCode,
      planType,
      category
    },
  };
}

// Driver
// urls & page config may not be as expected
// they are sometimes strange, eg. type=add means add2cart
// (1, parse url & page config; 2, flatten the diff) => change page config => target flow

export default [
  {
    // generateCartId=addQty
    test: ({ cartid }) => {
      const cartType = get(window, 'newBillingConfig.preloadCartInfo.configData.type');
      return cartid && cartType === SHARE_TYPE_ENUM.ADD_QTY;
    },
    config() {
      const {from, plan} = get(window, 'newBillingConfig.preloadCartInfo.configData', {});
      return {
        type: flowTypes.edit, // gets into edit flow
        // oldPlanCode
        editConfig: {
          // oldPlanCode: should be populated in editFlow::initShoppingCart
          planType: getPlanTypeByCategory(from || plan || 'pro'),
          category: from || plan || 'pro'
        },
      };
    },
  },
  {
    test: ({ cartid }) => {
      return cartid;
    },
    config() {
      const configData = get(window, 'newBillingConfig.preloadCartInfo.configData', {});
      const { type, from, plan } = configData;
      if (type === flowTypes.edit) {
        return editConfigParse(configData);
      } else if (type === flowTypes.add2cart) {
        return add2cartConfigParse(configData);
      }
      return {
        type: type || flowTypes.buyNow,
        // oldPlanCode
        editConfig: {
          // oldPlanCode: should be populated in editFlow::initShoppingCart
          planType: getPlanTypeByCategory(from || plan || 'pro'),
          category: from || plan || 'pro'
        },
      };
    },
  },
  {
    // buynow
    test: ({ type }) => (type === flowTypes.buyNow || isEmptyVal(type)),
    config({ from, plan }) {
      return {
        type: flowTypes.buyNow,
        editConfig: {
          planType: getPlanTypeByCategory(from || plan || 'pro'),
          category: from || plan || 'pro'
        },
      };
    },
  },
  {
    // basic2pro
    test: ({ type }) => type === flowTypes.basic2pro,
    config({ type, from, plan, ref }) {
      const isWinback = /^winback$/i.test(ref);
      return {
        type,
        isWinback,
        editConfig: {
          planType: getPlanTypeByCategory(from || plan || 'pro'),
          category: from || plan || 'pro'
        },
      };
    },
  },
  {
    // pro2biz
    test: ({ type }) => type === flowTypes.pro2biz,
    config({ type, plan = BASE_PLAN_SUB_TYPES.BIZ }) {
      return {
        type,
        editConfig: {
          plan,
          targetCategory: plan,
          planType: planTypes.ZOOM_MEETING,
          sourcePlanType: planTypes.ZOOM_MEETING,
        },
      };
    },
  },
  {
    // upgrade
    test: ({ type }) => type === flowTypes.upgrade,
    config({ type, plan, oldPlanCode = "" }) {
      return {
        type,
        editConfig: {
          plan,
          targetCategory: plan,
          planType: planTypes.ZOOM_MEETING,
          oldPlanCode
        },
      };
    },
  },
  {
    // add2cart
    test: ({ type, subPlan }) => (type === 'add' || type === 'add2cart') && subPlan === 'newsubs',
    config: add2cartConfigParse,
  },
  {
    // edit
    test: ({ type }) => type === flowTypes.edit,
    config: editConfigParse,
  },
  {
    // downgrade
    test: ({ type }) => type === flowTypes.downgrade,
    config({ type, plan, oldPlanCode, from }) {
      const planType = getPlanTypeByCategory(from || plan);
      return {
        type,
        editConfig: {
          plan,
          oldPlanCode,
          planType,
          category: from || plan,
        },
      };
    },
  },
  {
    // biz2pro
    test: ({ type }) => type === flowTypes.biz2pro,
    config({ type, from, plan = BASE_PLAN_SUB_TYPES.PRO }) {
      return {
        type,
        editConfig: {
          plan,
          planType: planTypes.ZOOM_MEETING,
          category: from || 'pro'
        },
      };
    },
  },
  {
    // trial2paid
    test: ({ type }) => type === flowTypes.trial2paid,
    config({ subPlan, oldPlanCode, campaign_code, trial_code /* FREE TRIAL - online free trial plan upgrade to paid plan */ }) {
      const planType = getPlanTypeByCategory(subPlan);
      return {
        editConfig: {
          oldPlanCode,
          planType,
          category: subPlan,
          campaign_code,
          trial_code
        },
      };
    },
  },
  {
    // BUY TRIAL - buy trial
    test: ({ type }) => type === flowTypes.buyTrial,
    config({ from_sub, from, campaign_code }) {
      return {
        type: flowTypes.buyTrial,
        editConfig: {
          planType: getPlanTypeByCategory(from_sub || from),
          category: from_sub || from,
          campaign_code
        },
      };
    }
  },
  {
    // FREE TRIAL - add2trial
    test: ({ type }) => type === flowTypes.add2trial,
    config({ from_sub, from, campaign_code }) {
      return {
        type: flowTypes.add2trial,
        editConfig: {
          planType: getPlanTypeByCategory(from_sub || from),
          category: from_sub || from,
          campaign_code
        },
      };
    }
  },
  {
    // mix buy
    test: ({ type }) => type === flowTypes.mixBuy,
    config() {
      return {
        type: flowTypes.mixBuy,
        editConfig: {
          planType: "",
          category: "",
        },
      };
    },
  },
  {
    // biz2bundle/pro2bundle
    test: ({ type }) => [flowTypes.biz2bundle, flowTypes.pro2bundle].includes(type),
    config({ type }) {
      return {
        type,
        editConfig: {
          planType: planTypes.ZOOM_ONE_BUNDLE,
          sourcePlanType: planTypes.ZOOM_MEETING,
        },
      };
    },
  },
  {
    // pro2zone_proplus
    test: ({ type }) => type === flowTypes.pro2prop,
    config() {
      return {
        type: flowTypes.pro2prop,
        editConfig: {
          planType: planTypes.ZONE_PRO_PLUS,
          sourcePlanType: planTypes.ZOOM_MEETING,
        },
      };
    },
  },
  {
    // prop2bundle
    test: ({ type }) => type === flowTypes.prop2bundle,
    config() {
      return {
        type: flowTypes.prop2bundle,
        editConfig: {
          planType: planTypes.ZOOM_ONE_BUNDLE,
          sourcePlanType: planTypes.ZONE_PRO_PLUS,
        },
      };
    },
  },
  {
    // zs2ze
    test: ({ type }) => type === flowTypes.zs2ze,
    config() {
      return {
        type: flowTypes.zs2ze,
        editConfig: {
          planType: planTypes.EVENT_SAAS
        },
      };
    },
  },
  {
    // switch
    test: ({ type }) => type === flowTypes.switch,
    config({from}) {
      return {
        type: flowTypes.switch,
        editConfig: {
          planType: getPlanTypeByCategory(from),
        },
      };
    },
  },
  {
    // eduschool => eduschoolplus
    test: ({ type }) => type === flowTypes.esc2escp,
    config({ type }) {
      return {
        type,
        editConfig: {
          planType: planTypes.ZONE_EDU_PLUS,
          sourcePlanType: planTypes.ZOOM_MEETING,
        },
      };
    },
  },
  {
    // eduschool => eduschoolplus
    test: ({ type }) => type === flowTypes.edu2esc,
    config({ type, plan, oldPlanCode = "" }) {
      return {
        type,
        editConfig: {
          plan,
          planType: planTypes.ZONE_EDU_PLUS,
          sourcePlanType: planTypes.ZOOM_MEETING,
          oldPlanCode
        },
      };
    },
  },
  {
    // pro2biz
    test: ({ type }) => type === flowTypes.pbx2prop,
    config({ type, plan = 'pbx', oldPlanCode }) {
      return {
        type,
        editConfig: {
          plan,
          oldPlanCode
        },
      };
    },
  },
];
